import React, { useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import styles from "../../../styles/components/StripeCheckoutModal.module.css";

function useCurrentTheme() {
  const [isDark, setIsDark] = useState(
    document.documentElement.dataset.theme === "dark"
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.dataset.theme === "dark");
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  return isDark;
}

function StripeSetupForm({ clientSecret, cardElementStyle, onClose, onDone }) {
  const stripe = useStripe();
  const elements = useElements();
  const [cardholderName, setCardholderName] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const handleConfirm = async () => {
    if (!stripe || !elements) return;

    setSaving(true);
    setErr("");

    const cardNumberElement = elements.getElement(CardNumberElement);
    const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: cardNumberElement,
        billing_details: { name: cardholderName.trim() || undefined },
      },
    });

    setSaving(false);

    if (error) {
      setErr(error.message || "No se pudo guardar el método de pago.");
      return;
    }

    onDone?.({ setupIntentId: setupIntent?.id, paymentMethodId: setupIntent?.payment_method });
  };

  return (
    <div className={styles.formBody}>
      <div className={styles.formHint}>
        Completá los datos de la tarjeta. No se realizará ningún cobro ahora.
      </div>

      <div className={styles.cardFieldsForm}>
        <div className={styles.cardField}>
          <span className={styles.cardFieldLabel}>Nombre del titular</span>
          <input
            type="text"
            className={styles.cardFieldTextInput}
            placeholder="Como aparece en la tarjeta"
            value={cardholderName}
            onChange={(e) => setCardholderName(e.target.value)}
            disabled={saving}
            autoComplete="cc-name"
          />
        </div>

        <div className={styles.cardField}>
          <span className={styles.cardFieldLabel}>Número de tarjeta</span>
          <div className={styles.cardFieldInput}>
            <CardNumberElement options={{ style: cardElementStyle }} />
          </div>
        </div>

        <div className={styles.cardFieldRow}>
          <div className={styles.cardField}>
            <span className={styles.cardFieldLabel}>Vencimiento</span>
            <div className={styles.cardFieldInput}>
              <CardExpiryElement options={{ style: cardElementStyle }} />
            </div>
          </div>

          <div className={styles.cardField}>
            <span className={styles.cardFieldLabel}>CVC</span>
            <div className={styles.cardFieldInput}>
              <CardCvcElement options={{ style: cardElementStyle }} />
            </div>
          </div>
        </div>
      </div>

      {err && <div className={styles.formError}>{err}</div>}

      <div className={styles.formActions}>
        <button
          type="button"
          className={styles.cancelButton}
          onClick={onClose}
          disabled={saving}
        >
          Cancelar
        </button>
        <button
          type="button"
          className={styles.confirmButton}
          onClick={handleConfirm}
          disabled={saving || !stripe}
        >
          {saving ? "Guardando..." : "Guardar tarjeta"}
        </button>
      </div>
    </div>
  );
}

export default function AddPaymentMethodModal({ open, onClose, clientSecret, onDone }) {
  const pk = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  const isDark = useCurrentTheme();

  const stripePromise = useMemo(() => {
    if (!pk) return null;
    return loadStripe(pk);
  }, [pk]);

  const cardElementStyle = useMemo(() => ({
    base: {
      fontSize: "15px",
      fontFamily: "DM Sans, sans-serif",
      color: isDark ? "#ffffff" : "#1a1a1b",
      "::placeholder": { color: isDark ? "#666" : "#aaa" },
    },
    invalid: { color: isDark ? "#f87171" : "#ef4444" },
  }), [isDark]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className={styles.backdrop}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>Agregar tarjeta</div>
          <button type="button" className={styles.closeButton} onClick={onClose}>
            ✕
          </button>
        </div>
        <div className={styles.modalBody}>
          {!pk && (
            <div className={styles.configError}>
              Falta configuración: no está seteada <b>REACT_APP_STRIPE_PUBLISHABLE_KEY</b>.
            </div>
          )}
          {pk && !clientSecret && (
            <div className={styles.configError}>
              Falta <b>client_secret</b> del SetupIntent.
            </div>
          )}
          {pk && clientSecret && stripePromise && (
            <Elements stripe={stripePromise}>
              <StripeSetupForm
                clientSecret={clientSecret}
                cardElementStyle={cardElementStyle}
                onClose={onClose}
                onDone={onDone}
              />
            </Elements>
          )}
        </div>
      </div>
    </div>
  );
}
